<template>
    <Modal ref="modal" :title paddingless :size="selectedTicketSellers.length > 2 ? 'is-fullwidth' : 'is-4xlarge'">
        <LoadingSpinner v-if="isLoading" label="Storing Initial Tickets" />
        <p v-if="needsInitialSetup" class="tw-px-6 tw-pt-4">
            Give your first ticket a name to get started. Once you have done this you can then set a price and
            allocation.
            <br />
            You can also add additional sales channels by clicking the "Add Seller" button.
        </p>
        <DataTable
            :columns="
                [
                    {
                        title: 'Name',
                        field: 'name',
                        width: 200,
                    },
                    !hasNoRevenueStream
                        ? {
                              title: 'Revenue Stream',
                              field: 'revenue_stream',
                          }
                        : null,
                    !hasNoRevenueStream
                        ? {
                              title: 'Category',
                              field: 'category',
                          }
                        : null,
                    {
                        title: 'Type',
                        field: 'payment_structure',
                    },
                    {
                        title: 'Price',
                        field: 'price',
                    },
                    {
                        title: 'Headcount',
                        field: 'headcount',
                    },
                    {
                        title: 'Fixed',
                        field: 'fixed_tickets',
                    },
                    ...selectedTicketSellers.map((seller, index) => ({
                        title: seller.name,
                        field: 'seller',
                        seller: seller,
                        index: index,
                    })),
                    {
                        title: '',
                        field: 'actions',
                        headerClass: 'tw-text-right',
                        class: 'tw-text-right',
                    },
                ].filter(Boolean)
            "
            :data="ticketTypes"
        >
            <template #heading(seller)="{ column }">
                <div class="tw-text-center">
                    <div>
                        {{ column.seller.name }}
                        <Button
                            type="is-danger"
                            class="tw-ml-[1px] !tw-py-0 !tw-px-1"
                            @click="removeTicketSeller(column.seller)"
                        >
                            <i class="mdi mdi-minus tw-text-sm"></i>
                        </Button>
                    </div>
                    <div class="tw-flex tw-justify-evenly">
                        <div>Allocated</div>
                        <div>Rebate</div>
                    </div>
                </div>
            </template>
            <template #heading(actions)>
                <Dropdown placement="bottom-end" :distance="0">
                    <template #trigger>
                        <Button>Add Seller</Button>
                    </template>
                    <ul class="context-menu">
                        <li
                            v-for="ticketSeller in availableSellers.filter(
                                (s) => !selectedTicketSellers.find((selected) => selected.name === s.name)
                            )"
                            :key="ticketSeller.name"
                        >
                            <button v-close-popper @click="addExistingTicketSeller(ticketSeller)">
                                {{ ticketSeller.name }}
                            </button>
                        </li>
                        <li
                            v-if="
                                availableSellers.filter(
                                    (s) => !selectedTicketSellers.find((selected) => selected.name === s.name)
                                ).length
                            "
                            class="separator"
                        ></li>
                        <li>
                            <button v-close-popper @click="addNewTicketSeller()">
                                <i class="mdi mdi-plus mr-1"></i>
                                New Ticket Seller
                            </button>
                        </li>
                    </ul>
                </Dropdown>
            </template>
            <template #column(name)="{ index }">
                <EditableText v-model="ticketTypes[index].name" placeholder="Required" />
            </template>
            <template #column(payment_structure)="{ index }">
                <Button
                    type="is-link"
                    compact
                    class="tw-whitespace-nowrap"
                    @click="
                        ticketTypes[index].payment_structure =
                            ticketTypes[index].payment_structure === 'upfront' ? 'payment_plan' : 'upfront'
                    "
                >
                    {{ ticketTypes[index].payment_structure === 'upfront' ? 'Upfront' : 'Payment Plan' }}
                </Button>
            </template>
            <template #column(revenue_stream)="{ index }">
                <Select v-model="ticketTypes[index].department_id">
                    <option
                        v-for="revenueDepartment in revenueDepartments"
                        :key="revenueDepartment.id"
                        :value="revenueDepartment.id"
                    >
                        {{ revenueDepartment.name }}
                    </option>
                </Select>
            </template>
            <template #column(category)="{ index }">
                <Select v-model="ticketTypes[index].category_id" required>
                    <option v-for="category in availableCategories(index)" :key="category.id" :value="category.id">
                        {{ category.name }}
                    </option>
                </Select>
            </template>
            <template #column(price)="{ index }">
                <EditableText
                    v-model="ticketTypes[index].price"
                    type="currency"
                    :min-value="0"
                    placeholder="Required"
                />
            </template>
            <template #column(headcount)="{ index }">
                <NumberInput
                    v-model="ticketTypes[index].headcount"
                    type="number"
                    :min-value="0"
                    class="tw-max-w-[5rem]"
                    placeholder="Optional"
                />
            </template>
            <template #column(fixed_tickets)="{ index }">
                <NumberInput
                    :model-value="getTicketSaleValue(index, 0, 'fixed_tickets')"
                    :name="`ticket-${index}-seller-allocated`"
                    input-class="tw-max-w-[5rem]"
                    @update:model-value="setTicketSaleValue(index, 0, 'fixed_tickets', $event)"
                />
            </template>
            <template #column(direct_sales)="{ index }">
                <div class="tw-flex tw-gap-3 tw-justify-center">
                    <NumberInput
                        :model-value="getTicketSaleValue(index, 0, 'allocated')"
                        :name="`ticket-${index}-direct-sales-allocated`"
                        input-class="tw-max-w-[7rem]"
                        @update:model-value="setTicketSaleValue(index, 0, 'allocated', $event)"
                    />
                    <PercentageValue
                        v-if="Auth.can('manage tickets')"
                        :model-value="getTicketSaleValue(index, 0, 'kickback_cost')"
                        :base-value="ticketTypes[index].price"
                        :currency="true"
                        :step="0.0001"
                        size="is-small"
                        input-class="tw-max-w-[7rem]"
                        @update:model-value="setTicketSaleValue(index, 0, 'kickback_cost', $event)"
                    />
                </div>
            </template>
            <template #column(seller)="{ index, column }">
                <div class="tw-flex tw-gap-3 tw-justify-center">
                    <NumberInput
                        :model-value="getTicketSaleValue(index, column.index, 'allocated')"
                        :name="`ticket-${index}-seller-allocated`"
                        input-class="tw-max-w-[7rem]"
                        @update:model-value="setTicketSaleValue(index, column.index, 'allocated', $event)"
                    />
                    <PercentageValue
                        v-if="Auth.can('manage tickets')"
                        :model-value="getTicketSaleValue(index, column.index, 'kickback_cost')"
                        :base-value="ticketTypes[index].price"
                        :currency="true"
                        :step="0.0001"
                        size="is-small"
                        input-class="tw-max-w-[7rem]"
                        @update:model-value="setTicketSaleValue(index, column.index, 'kickback_cost', $event)"
                    />
                </div>
            </template>
            <template #column(actions)="{ index }">
                <Button type="is-danger" @click="removeTicketType(index)">Remove</Button>
            </template>
        </DataTable>
        <Button type="is-link" class="tw-mt-0 tw-w-full" @click="addNewTicketType()">
            <i class="mdi mdi-plus"></i>
            Add another ticket
        </Button>

        <Banner v-if="hasNoRevenueStream" type="is-warning" class="w-100 tw-mt-2">
            You do not have a revenue stream set up for tickets, one will be created for you when creating your tickets.
        </Banner>
        <template #footer>
            <FormField label="Tax Rate">
                <TaxRateSelector v-model="taxRateId" type="income" />
            </FormField>
            <div class="tw-flex-1"></div>
            <Button
                v-if="needsInitialSetup"
                type="is-link"
                @click="
                    settings.setEvent(store.get('current_event.id'), 'dismissInitialTicketScreen', true);
                    modal.close();
                "
            >
                I'll do this later
            </Button>
            <Button type="is-primary" :disabled="!ticketTypes.length" :loading="isLoading" @click="createTickets">
                Create Tickets
            </Button>
        </template>
    </Modal>
</template>
<script setup>
import { useDataStore } from '@/js/stores/DataStore.js';
import { useSettingsStore } from '@/js/stores/SettingsStore.js';
import TaxRateSelector from '@/js/components/controls/TaxRateSelector.vue';
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';
import Button from '@/js/components/controls/Button.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import { ref, computed, useTemplateRef, watch } from 'vue';
import { confirm, prompt } from '@/js/composables/useModals.ts';
import Modal from '@/js/components/Modal.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import PercentageValue from '@/js/components/widgets/PercentageValue.vue';
import Select from '@/js/components/controls/Select.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import DataTable from '@/js/components/tables/DataTable.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Dropdown from '@/js/components/controls/Dropdown.vue';
import { normaliseCreateTicketsPayload } from '@/js/utils/createTickets.ts';
import axios from 'axios';
import { slug } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const store = useDataStore();
const isLoading = ref(false);
const settings = useSettingsStore();
const needsInitialSetup = computed(() => Object.values(store.get('current_event.ticket_types')).length === 0);
const title = computed(() =>
    needsInitialSetup.value ? `Let's setup your tickets for ${store.get('current_event.name')}` : 'Add Tickets'
);
const modal = useTemplateRef('modal');
const taxRateId = ref(null);
const ticketTypes = ref([]);

watch(
    () => ticketTypes.value.map((ticketType) => ticketType.department_id),
    (newIds, oldIds) => {
        const changedIndex = newIds.findIndex((id, i) => id !== oldIds?.[i]);
        if (changedIndex !== -1) {
            const revenueDepartment = revenueDepartments.value.find(
                (department) => department.id === newIds[changedIndex]
            );
            if (revenueDepartment !== undefined) {
                taxRateId.value = revenueDepartment.default_tax_rate_id ?? null;
            }
        }
    }
);
const revenueDepartments = ref([]);
const categories = ref([]);
const selectedTicketSellers = ref([]);
const newTicketSellers = ref([]);

const getTicketSaleValue = (ticketTypeIndex, saleIndex, key) => {
    if (key === 'fixed_tickets') {
        return ticketTypes.value[ticketTypeIndex]?.fixed_tickets ?? 0;
    }

    const sale = ticketTypes.value[ticketTypeIndex]?.sales?.[saleIndex];
    return sale?.[key] ?? 0;
};
const setTicketSaleValue = (ticketTypeIndex, saleIndex, key, value) => {
    if (key === 'fixed_tickets') {
        const ticketType = ticketTypes.value[ticketTypeIndex];
        if (!ticketType) {
            return;
        }
        ticketType.fixed_tickets = value;
        return;
    }

    const sale = ticketTypes.value[ticketTypeIndex]?.sales?.[saleIndex];
    if (!sale) {
        return;
    }
    sale[key] = value;
};

const addNewTicketType = () => {
    ticketTypes.value.push({
        name: '',
        price: 0,
        payment_structure: 'upfront',
        fixed_tickets: 0,
        department_id: revenueDepartments.value.at(0)?.id,
        category_id: categories.value.at(0)?.id,
        sales: selectedTicketSellers.value.map((ticketSeller) => ({
            supplier_id: ticketSeller.id,
            allocated: 0,
            kickback_cost: 0,
            fixed_tickets: 0,
        })),
    });
};
const removeTicketType = async (index) => {
    const ticketType = ticketTypes.value[index];
    if (!ticketType) {
        return;
    }
    const result = await confirm({
        title: 'Remove Ticket',
        message: `Are you sure you want to remove ${ticketType.name}?`,
        confirmText: 'Yes',
        confirmType: 'is-danger',
    });
    if (!result) {
        return;
    }
    ticketTypes.value.splice(index, 1);
};

Eventbus.$on('create-tickets:open', (data) => {
    const { department = null, category = null } = data || {};
    open({ department, category });
});
const open = (data = {}) => {
    const revenueDepartment = data?.department ?? null;
    const category = data?.category ?? null;
    getExistingTicketSellers();
    selectedTicketSellers.value = getExistingTicketSellers();
    taxRateId.value = null;
    ticketTypes.value = [];
    const revenueDepartmentsList = Object.values(store.get('current_event.revenue_departments')).filter(
        (department) => department.revenue_stream_type === 0
    );
    if (revenueDepartment) {
        revenueDepartments.value = [
            revenueDepartmentsList.find((department) => department.id === revenueDepartment),
        ].filter((department) => !!department);
    } else {
        revenueDepartments.value = revenueDepartmentsList;
    }
    taxRateId.value = revenueDepartments.value.at(0)?.default_tax_rate_id ?? null;
    const categoriesList = Object.values(store.get('current_event.categories')).filter((c) => c.stream_type === 0);
    if (category) {
        categories.value = [categoriesList.find((c) => c.id === category)].filter((c) => !!c);
    } else {
        categories.value = categoriesList;
    }

    modal.value.open();
    addNewTicketType();
};

function getExistingTicketSellers() {
    const existingTicketSellerIds = Object.values(useDataStore().get('current_event.ticket_types'))
        .map((t) => t.ticket_sales)
        .flat()
        .map((sale) => sale.supplier_id);
    return availableSellers.value.filter((seller) => existingTicketSellerIds.includes(seller.id));
}

const addExistingTicketSeller = (ticketSeller) => {
    ticketTypes.value.forEach((ticketType, ticketTypeIndex) => {
        ticketTypes.value[ticketTypeIndex].sales.push({
            supplier_id: ticketSeller.id,
            allocated: 0,
            kickback_cost: 0,
            fixed_tickets: 0,
        });
    });
    selectedTicketSellers.value.push(ticketSeller);
};
const addNewTicketSeller = async () => {
    const input = await prompt({
        title: 'New Ticket Seller',
        message: 'Enter a name for the ticket seller',
        placeholder: 'Big Ticket Co.',
        defaultValue: '',
        confirmText: 'Add',
        cancelText: 'Cancel',
        validator: (value) => {
            if (availableSellers.value.find((seller) => seller.name === value))
                return 'A ticket seller with this name already exists';
            if (selectedTicketSellers.value.find((seller) => seller.name === value))
                return 'A ticket seller with this name already exists';
            return undefined; // Valid
        },
    });

    if (!input) {
        return;
    }

    newTicketSellers.value.push({
        id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}-${slug(input)}`,
        name: input,
        supplier_type: 1,
        is_new: true,
    });
    ticketTypes.value.forEach((ticketType, ticketTypeIndex) => {
        ticketTypes.value[ticketTypeIndex].sales.push({
            supplier_id: newTicketSellers.value.at(-1).id,
            allocated: 0,
            kickback_cost: 0,
            fixed_tickets: 0,
        });
    });
    selectedTicketSellers.value.push(newTicketSellers.value.at(-1));
};
const removeTicketSeller = async (seller, index) => {
    const result = await confirm({
        title: 'Remove Ticket',
        message: `Are you sure you want to remove ${seller.name ? seller.name : 'this ticket seller'} from your tickets?`,
        confirmText: 'Yes',
        cancelText: 'Cancel',
        confirmType: 'is-primary',
    });
    if (!result) {
        return;
    }
    ticketTypes.value = ticketTypes.value.map((ticketType) => {
        return {
            ...ticketType,
            sales: ticketType.sales.filter((sale) => sale.supplier_id !== seller.id),
        };
    });

    selectedTicketSellers.value.splice(
        selectedTicketSellers.value.findIndex((s) => s.id === seller.id),
        1
    );
};
const createTickets = async () => {
    isLoading.value = true;
    try {
        const ticketsPayload = normaliseCreateTicketsPayload(ticketTypes.value, selectedTicketSellers.value);

        await axios.post(route('api.account.event.tickets.initial'), {
            tax_rate_id: taxRateId.value,
            tickets: ticketsPayload,
            sellers: selectedTicketSellers.value,
        });
        ticketsPayload.forEach(() => capturePosthogEvent('ticket_created'));
        const result = await confirm({
            title: 'Tickets Created',
            message:
                'Your tickets have been set up successfully! Would you like to go to the Tickets page to make further changes?',
            confirmText: 'Visit Tickets',
            cancelText: 'No thanks',
        });
        if (!result) {
            modal.value.close();
            return;
        }
        window.location.href = route('account.event.tickets', {
            page: 'bulk',
        });
    } finally {
        isLoading.value = false;
    }
};

const hasNoRevenueStream = computed(
    () =>
        !Object.values(store.get('current_event.revenue_departments')).filter(
            (department) => department.revenue_stream_type === 0
        ).length
);

const availableSellers = computed(() => {
    // Filter by supplier type 1 (Ticket Sellers)
    const currentTicketSellers = Object.values(useDataStore().get('account.suppliers')).filter(
        (supplier) => supplier.supplier_type === 1
    );

    return [
        ...currentTicketSellers,
        ...newTicketSellers.value.filter(
            (newSeller) => !currentTicketSellers.find((seller) => seller.id === newSeller.id)
        ),
    ];
});
const availableCategories = (index) => {
    const departmentId = ticketTypes.value[index].department_id;

    return Object.values(store.get('current_event.categories')).filter(
        (c) => c.stream_type === 0 && c.department_id === departmentId
    );
};
</script>
